(() => {
  const ELEMENT_NAME = 'collection-infinite-scroll';
  const CACHE_VERSION = 2;
  const CACHE_TTL = 30 * 60 * 1000;
  const CACHE_MAX_BYTES = 2.5 * 1024 * 1024;
  const CACHE_INDEX_KEY = 'craftoria:collection-cache:index:v2';
  const CACHE_PAGE_PREFIX = 'craftoria:collection-cache:page:v2';
  const RETURN_STATE_KEY = 'craftoriaCollectionReturn';
  const RETURN_STORAGE_KEY = 'craftoria:collection-return:v2';

  if (!window.customElements || customElements.get(ELEMENT_NAME)) return;

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.connectionGeneration = (this.connectionGeneration || 0) + 1;
      this.productGrid = this.querySelector('[data-infinite-scroll-products]');
      this.controls = this.querySelector('[data-infinite-scroll-controls]');
      this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
      this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
      this.button = this.querySelector('[data-infinite-scroll-button]');
      this.status = this.querySelector('[data-infinite-scroll-status]');

      if (!this.productGrid || !this.controls || !this.sentinel || !this.spinner || !this.button || !this.status) return;

      this.autoLoadLimit = Number.parseInt(this.getAttribute('auto-load-limit'), 10) || 200;
      this.initialCount = this.productGrid.children.length;
      this.initialSignature = this.getInitialSignature();
      this.loadedCount = this.initialCount;
      this.nextUrl = this.getAttribute('next-url') || '';
      this.bufferedProducts = [];
      this.loadedPages = [];
      this.loading = false;
      this.restoring = false;
      this.scrollRestoreScheduled = false;
      this.manualMode = this.loadedCount >= this.autoLoadLimit;
      this.abortController = null;
      this.cacheDisabled = false;
      this.cacheLimitReached = false;
      this.cachedBytes = 0;
      this.cacheIdleId = null;
      this.previousScrollRestoration = null;
      this.scrollRestorationOverridden = false;
      this.scrollStabilizationController = null;
      this.scrollResizeObserver = null;
      this.scrollAlignmentFrame = null;
      this.scrollFinishTimer = null;
      this.scrollRestorationReleaseHandler = null;
      this.scrollCorrectionCancelled = false;

      this.handleButtonClick = () => this.loadNextPage(true);
      this.handleProductClick = (event) => this.saveReturnPosition(event);
      this.handlePageShow = (event) => this.consumeBfcacheReturnState(event);
      this.button.addEventListener('click', this.handleButtonClick);
      this.productGrid.addEventListener('click', this.handleProductClick, true);
      window.addEventListener('pageshow', this.handlePageShow);
      this.classList.add('is-enhanced');

      this.forceManualMode = !('IntersectionObserver' in window) || Boolean(window.Shopify && window.Shopify.designMode);

      if (this.forceManualMode) {
        this.manualMode = true;
      } else {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage(false);
          },
          { rootMargin: '800px 0px' }
        );
      }

      const returnState = this.getReturnState();

      if (returnState) {
        this.cacheId = returnState.cacheId;
        this.restoreCollection(returnState);
      } else {
        this.startNewCache();
        this.updateControls();
      }
    }

    disconnectedCallback() {
      this.connectionGeneration = (this.connectionGeneration || 0) + 1;
      this.button?.removeEventListener('click', this.handleButtonClick);
      this.productGrid?.removeEventListener('click', this.handleProductClick, true);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.observer?.disconnect();
      this.abortController?.abort();
      this.cancelScheduledCache();
      this.scrollCorrectionCancelled = true;
      this.stopScrollStabilization();
      this.releaseScrollRestoration(true);
    }

    async loadNextPage(manualRequest) {
      if (this.loading || this.restoring || (!this.nextUrl && this.bufferedProducts.length === 0)) return;
      if (!manualRequest && this.manualMode) return;

      const generation = this.connectionGeneration;
      this.loading = true;
      this.observer?.unobserve(this.sentinel);
      this.updateControls();

      try {
        let products;
        let pageDescriptor = null;

        if (this.bufferedProducts.length > 0) {
          products = this.bufferedProducts;
          this.bufferedProducts = [];
          pageDescriptor = this.loadedPages[this.loadedPages.length - 1] || null;
        } else {
          const page = await this.fetchPage(this.nextUrl);

          if (!this.isConnected || generation !== this.connectionGeneration) return;

          products = page.products;
          this.nextUrl = page.nextUrl;
          pageDescriptor = this.createPageDescriptor(page);
          this.loadedPages.push(pageDescriptor);
          this.schedulePageCache();
        }

        if (!manualRequest) {
          const remainingAutoSlots = Math.max(this.autoLoadLimit - this.loadedCount, 0);

          if (products.length > remainingAutoSlots) {
            this.bufferedProducts = products.slice(remainingAutoSlots);
            products = products.slice(0, remainingAutoSlots);
          }
        }

        this.appendProducts(products);
        this.loadedCount += products.length;

        if (pageDescriptor) pageDescriptor.displayedCount += products.length;
        if (this.loadedCount >= this.autoLoadLimit) this.manualMode = true;

        this.status.textContent = this.dataset.messageLoaded ? this.dataset.messageLoaded.replace('__CRAFTORIA_ADDED__', String(products.length)).replace('__CRAFTORIA_TOTAL__', String(this.loadedCount)) : `${products.length} more products loaded. ${this.loadedCount} products shown.`;
        this.dispatchProductsEvent('collection:products-loaded', products.length);
      } catch (error) {
        if (generation !== this.connectionGeneration) return;

        if (error.name !== 'AbortError') {
          this.manualMode = true;
          this.status.textContent = this.dataset.messageLoadError || 'More products could not be loaded. Select Load more to try again.';
        }
      } finally {
        if (generation !== this.connectionGeneration) return;

        this.loading = false;

        if (this.isConnected) this.updateControls();
      }
    }

    async fetchPage(pageUrl, signal) {
      if (!signal) {
        this.abortController = new AbortController();
        signal = this.abortController.signal;
      }

      const requestUrl = new URL(pageUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.getAttribute('section-id'));

      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal
      });

      if (!response.ok) throw new Error(`Collection page request failed with ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextLoader = responseDocument.querySelector(ELEMENT_NAME);
      const nextGrid = nextLoader?.querySelector('[data-infinite-scroll-products]');

      if (!nextLoader || !nextGrid) throw new Error('Collection products were missing from the response');

      const cleanRequestUrl = new URL(requestUrl);
      cleanRequestUrl.searchParams.delete('section_id');

      return {
        products: Array.from(nextGrid.children),
        markup: nextGrid.innerHTML,
        nextUrl: nextLoader.getAttribute('next-url') || '',
        requestUrl: `${cleanRequestUrl.pathname}${cleanRequestUrl.search}`
      };
    }

    createPageDescriptor(page) {
      return {
        url: page.requestUrl,
        displayedCount: 0,
        storageKey: `${CACHE_PAGE_PREFIX}:${this.cacheId}:${this.loadedPages.length}`,
        cached: false,
        cacheSkipped: false,
        markup: page.markup
      };
    }

    appendProducts(products) {
      const fragment = document.createDocumentFragment();

      products.forEach((product) => {
        product.removeAttribute('reveal-on-scroll');
        fragment.appendChild(product);
      });

      this.productGrid.appendChild(fragment);
    }

    dispatchProductsEvent(name, count) {
      this.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: { count, totalShown: this.loadedCount }
      }));
    }

    updateControls() {
      const hasMoreProducts = Boolean(this.nextUrl || this.bufferedProducts.length > 0);
      this.controls.hidden = !hasMoreProducts;
      this.spinner.hidden = !this.loading && !this.restoring;
      this.button.hidden = !hasMoreProducts || this.loading || this.restoring || !this.manualMode;
      this.button.disabled = this.loading || this.restoring;

      if (!this.loading && !this.restoring && hasMoreProducts && !this.manualMode && this.isConnected) {
        this.observer?.observe(this.sentinel);
      }
    }

    startNewCache() {
      this.clearPreviousCache();
      this.cacheId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.safeStorageRemove(RETURN_STORAGE_KEY);

      if (!this.saveCacheIndex([])) this.cacheDisabled = true;
    }

    clearPreviousCache() {
      const cacheIndex = this.safeStorageGet(CACHE_INDEX_KEY);
      const previousReturnState = this.safeStorageGet(RETURN_STORAGE_KEY);

      if (cacheIndex) {
        try {
          const parsedIndex = JSON.parse(cacheIndex);
          parsedIndex.keys?.forEach((key) => this.safeStorageRemove(key));
        } catch (error) {
          // A malformed index should not block collection browsing.
        }
      }

      if (previousReturnState) {
        try {
          const parsedReturnState = JSON.parse(previousReturnState);
          parsedReturnState.pages?.forEach((page) => this.safeStorageRemove(page.storageKey));
        } catch (error) {
          // A malformed return state should not block collection browsing.
        }
      }

      this.clearOrphanedPageCache();
      this.safeStorageRemove(CACHE_INDEX_KEY);
    }

    clearOrphanedPageCache() {
      const matchingKeys = [];

      try {
        for (let index = 0; index < window.sessionStorage.length; index += 1) {
          const key = window.sessionStorage.key(index);

          if (key?.startsWith(`${CACHE_PAGE_PREFIX}:`)) matchingKeys.push(key);
        }
      } catch (error) {
        return;
      }

      matchingKeys.forEach((key) => this.safeStorageRemove(key));
    }

    schedulePageCache() {
      if (this.cacheDisabled || this.cacheLimitReached || this.cacheIdleId !== null) return;

      const savePages = () => {
        this.cacheIdleId = null;
        this.flushPageCache();
      };

      if ('requestIdleCallback' in window) {
        this.cacheIdleId = window.requestIdleCallback(savePages, { timeout: 3000 });
      } else {
        this.cacheIdleId = window.setTimeout(savePages, 500);
      }
    }

    cancelScheduledCache() {
      if (this.cacheIdleId === null) return;

      if ('cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.cacheIdleId);
      } else {
        window.clearTimeout(this.cacheIdleId);
      }

      this.cacheIdleId = null;
    }

    flushPageCache(flushAll = false) {
      if (this.cacheDisabled) return;

      const storedKeys = [];

      for (const page of this.loadedPages) {
        if (!page.cached && !page.cacheSkipped && page.markup) {
          const pageBytes = page.markup.length * 2;

          if (this.cachedBytes + pageBytes > CACHE_MAX_BYTES) {
            this.cacheLimitReached = true;
            page.cacheSkipped = true;
            page.markup = null;
            continue;
          }

          if (!this.safeStorageSet(page.storageKey, page.markup)) {
            this.cacheDisabled = true;
            break;
          }

          page.cached = true;
          page.byteLength = pageBytes;
          page.markup = null;
          this.cachedBytes += pageBytes;

          if (!flushAll) {
            storedKeys.push(page.storageKey);
            break;
          }
        }

        if (page.cached) storedKeys.push(page.storageKey);
      }

      if (!this.saveCacheIndex(storedKeys)) {
        storedKeys.forEach((key) => this.safeStorageRemove(key));
        this.loadedPages.forEach((page) => {
          page.cached = false;
          page.cacheSkipped = true;
          page.markup = null;
        });
        this.cachedBytes = 0;
        this.cacheDisabled = true;
      }

      if (!flushAll && !this.cacheDisabled && !this.cacheLimitReached && this.loadedPages.some((page) => !page.cached && !page.cacheSkipped)) {
        this.schedulePageCache();
      }
    }

    saveCacheIndex(keys) {
      return this.safeStorageSet(CACHE_INDEX_KEY, JSON.stringify({
        version: CACHE_VERSION,
        cacheId: this.cacheId,
        urlKey: this.getUrlKey(),
        keys,
        savedAt: Date.now()
      }));
    }

    saveReturnPosition(event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest('a[href]');

      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

      const linkUrl = new URL(link.href, window.location.origin);
      const productCard = link.closest('product-card');

      if (linkUrl.origin !== window.location.origin || !linkUrl.pathname.includes('/products/') || !productCard) return;

      this.cancelScheduledCache();
      this.flushPageCache();

      const productCards = Array.from(this.productGrid.children);
      const productRect = productCard.getBoundingClientRect();
      const returnState = {
        version: CACHE_VERSION,
        token: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        savedAt: Date.now(),
        cacheId: this.cacheId,
        urlKey: this.getUrlKey(),
        entryUrl: window.location.href,
        initialCount: this.initialCount,
        initialSignature: this.initialSignature,
        productHandle: productCard.getAttribute('handle') || '',
        productIndex: productCards.indexOf(productCard),
        productViewportTop: productRect.top,
        scrollY: window.scrollY,
        loadedCount: this.loadedCount,
        nextUrl: this.nextUrl,
        manualMode: this.manualMode,
        pages: this.loadedPages.map((page) => ({
          url: page.url,
          displayedCount: page.displayedCount,
          storageKey: page.storageKey
        }))
      };

      const currentHistoryState = history.state && typeof history.state === 'object' ? history.state : {};

      try {
        history.replaceState({ ...currentHistoryState, [RETURN_STATE_KEY]: returnState }, '', window.location.href);
      } catch (error) {
        // sessionStorage below remains as a fallback when history state is unavailable.
      }

      this.safeStorageSet(RETURN_STORAGE_KEY, JSON.stringify(returnState));
    }

    getReturnState() {
      const historyReturnState = history.state?.[RETURN_STATE_KEY];

      if (this.isValidReturnState(historyReturnState)) return historyReturnState;

      if (!this.isBackForwardNavigation()) return null;

      const storedReturnState = this.safeStorageGet(RETURN_STORAGE_KEY);

      if (!storedReturnState) return null;

      try {
        const parsedState = JSON.parse(storedReturnState);
        return this.isValidReturnState(parsedState) ? parsedState : null;
      } catch (error) {
        return null;
      }
    }

    consumeBfcacheReturnState(event) {
      if (!event.persisted) return;

      const returnState = history.state?.[RETURN_STATE_KEY];

      if (returnState?.urlKey === this.getUrlKey()) {
        requestAnimationFrame(() => window.setTimeout(() => {
          if (history.state?.[RETURN_STATE_KEY]?.token === returnState.token) {
            this.consumeReturnState(returnState);
          }
        }, 0));
      }
    }

    consumeReturnState(returnState) {
      window.__craftoriaCollectionReturnConsumed = returnState.token;
      this.safeStorageRemove(RETURN_STORAGE_KEY);

      const currentHistoryState = history.state;

      if (!currentHistoryState || typeof currentHistoryState !== 'object' || !(RETURN_STATE_KEY in currentHistoryState)) return;

      const nextHistoryState = { ...currentHistoryState };
      delete nextHistoryState[RETURN_STATE_KEY];

      try {
        history.replaceState(nextHistoryState, '', window.location.href);
      } catch (error) {
        // The in-memory token still prevents a duplicate restore in this document.
      }
    }

    isValidReturnState(returnState) {
      if (!returnState || returnState.version !== CACHE_VERSION || returnState.urlKey !== this.getUrlKey()) return false;
      if (Date.now() - returnState.savedAt > CACHE_TTL) return false;
      if (window.__craftoriaCollectionReturnConsumed === returnState.token) return false;

      return true;
    }

    isBackForwardNavigation() {
      const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
      return navigationEntry?.type === 'back_forward' || performance.navigation?.type === 2;
    }

    async restoreCollection(returnState) {
      const generation = this.connectionGeneration;
      this.consumeReturnState(returnState);
      this.restoring = true;
      this.scrollRestoreScheduled = false;
      this.observer?.disconnect();

      try {
        this.previousScrollRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
        this.scrollRestorationOverridden = true;
        this.scrollRestorationReleaseToken = (this.scrollRestorationReleaseToken || 0) + 1;
      } catch (error) {
        // Older browsers will still be corrected using the saved product anchor.
      }

      this.scrollCorrectionCancelled = false;
      this.startScrollStabilization();
      this.updateControls();

      try {
        if (returnState.loadedCount > this.initialCount && returnState.pages.length > 0) {
          await this.restoreLoadedPages(returnState, generation);
        } else {
          this.nextUrl = returnState.nextUrl;
          this.manualMode = Boolean(returnState.manualMode || this.forceManualMode);
        }

        if (!this.isConnected || generation !== this.connectionGeneration) return;

        this.status.textContent = this.dataset.messageRestored ? this.dataset.messageRestored.replace('__CRAFTORIA_TOTAL__', String(this.loadedCount)) : `${this.loadedCount} products restored.`;
        this.dispatchProductsEvent('collection:products-restored', this.loadedCount - this.initialCount);
        this.restoreScrollPosition(returnState);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.manualMode = true;
          this.status.textContent = this.dataset.messageRestoreError || 'Your previous products could not be restored automatically. Select Load more to continue.';
          this.restoreScrollPosition(returnState);
        }
      } finally {
        if (generation !== this.connectionGeneration) return;

        this.restoring = false;

        if (!this.scrollRestoreScheduled) {
          this.releaseScrollRestoration(true);
        }

        if (this.isConnected) this.updateControls();
      }
    }

    async restoreLoadedPages(returnState, generation = this.connectionGeneration) {
      this.abortController = new AbortController();
      const canUseCachedMarkup = this.initialSignature === returnState.initialSignature;
      const expectedLoadedCount = this.initialCount + returnState.pages.reduce(
        (count, page) => count + page.displayedCount,
        0
      );

      if (expectedLoadedCount !== returnState.loadedCount) {
        throw new Error('The saved collection product count was invalid');
      }

      const restoredPages = await Promise.all(returnState.pages.map(async (pageState) => {
        let products = [];
        let cached = false;
        let markup = null;
        let byteLength = 0;

        if (canUseCachedMarkup) {
          const storedMarkup = this.safeStorageGet(pageState.storageKey);

          if (storedMarkup) {
            products = this.parseProducts(storedMarkup);
            cached = products.length >= pageState.displayedCount;
            byteLength = storedMarkup.length * 2;
          }
        }

        if (!cached) {
          const fetchedPage = await this.fetchPage(pageState.url, this.abortController.signal);
          products = fetchedPage.products;
          markup = fetchedPage.markup;
        }

        if (products.length < pageState.displayedCount) {
          throw new Error('A restored collection page contained too few products');
        }

        return { products, cached, markup, byteLength };
      }));

      if (!this.isConnected || generation !== this.connectionGeneration) return;

      this.loadedPages = [];
      this.bufferedProducts = [];
      this.loadedCount = this.initialCount;
      this.cachedBytes = 0;
      this.cacheLimitReached = false;

      restoredPages.forEach((restoredPage, index) => {
        const pageState = returnState.pages[index];
        const shownProducts = restoredPage.products.slice(0, pageState.displayedCount);
        const remainingProducts = restoredPage.products.slice(pageState.displayedCount);

        this.appendProducts(shownProducts);
        this.loadedCount += shownProducts.length;

        const descriptor = {
          url: pageState.url,
          displayedCount: shownProducts.length,
          storageKey: pageState.storageKey,
          cached: restoredPage.cached,
          cacheSkipped: false,
          markup: restoredPage.markup,
          byteLength: restoredPage.byteLength
        };

        this.loadedPages.push(descriptor);

        if (descriptor.cached) this.cachedBytes += descriptor.byteLength;

        if (remainingProducts.length > 0 && index === restoredPages.length - 1) {
          this.bufferedProducts = remainingProducts;
        }
      });

      if (this.loadedCount !== returnState.loadedCount) {
        throw new Error('The restored collection product count did not match');
      }

      this.nextUrl = returnState.nextUrl;
      this.manualMode = Boolean(returnState.manualMode || this.forceManualMode);
      this.cacheLimitReached = this.cachedBytes >= CACHE_MAX_BYTES;
      this.schedulePageCache();
    }

    parseProducts(markup) {
      const template = document.createElement('template');
      template.innerHTML = markup;
      return Array.from(template.content.children);
    }

    restoreScrollPosition(returnState) {
      const generation = this.connectionGeneration;
      this.scrollRestoreScheduled = true;

      if (this.scrollCorrectionCancelled) {
        this.releaseScrollRestoration();
        return;
      }

      if (!this.scrollStabilizationController) this.startScrollStabilization();
      const stabilizationController = this.scrollStabilizationController;

      const alignToSavedProduct = () => {
        if (
          !this.isConnected
          || this.scrollCorrectionCancelled
          || generation !== this.connectionGeneration
          || stabilizationController.signal.aborted
        ) return;

        const productCards = Array.from(this.productGrid.children);
        const targetProduct = productCards.find((card) => card.getAttribute('handle') === returnState.productHandle)
          || productCards[returnState.productIndex];

        if (targetProduct) {
          const currentTop = targetProduct.getBoundingClientRect().top;
          const targetScrollY = window.scrollY + currentTop - returnState.productViewportTop;
          window.scrollTo({ top: Math.max(0, targetScrollY), left: 0, behavior: 'auto' });
        } else {
          window.scrollTo({ top: returnState.scrollY, left: 0, behavior: 'auto' });
        }
      };

      const finishRestoration = () => {
        if (generation !== this.connectionGeneration) return;

        if (!this.scrollCorrectionCancelled) alignToSavedProduct();
        this.stopScrollStabilization();
        this.releaseScrollRestoration();
      };

      const scheduleAlignment = () => {
        if (
          this.scrollCorrectionCancelled
          || generation !== this.connectionGeneration
          || stabilizationController.signal.aborted
          || this.scrollAlignmentFrame != null
        ) return;

        this.scrollAlignmentFrame = requestAnimationFrame(() => {
          this.scrollAlignmentFrame = null;
          alignToSavedProduct();
        });
      };

      if ('ResizeObserver' in window) {
        this.scrollResizeObserver = new window.ResizeObserver(scheduleAlignment);
        this.scrollResizeObserver.observe(this.productGrid);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(scheduleAlignment).catch(() => {});
      }

      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (stabilizationController.signal.aborted || generation !== this.connectionGeneration) return;

        alignToSavedProduct();

        if (document.readyState === 'complete') {
          this.scrollFinishTimer = window.setTimeout(finishRestoration, 1000);
        } else {
          window.addEventListener('pageshow', () => {
            requestAnimationFrame(() => {
              alignToSavedProduct();
              this.scrollFinishTimer = window.setTimeout(finishRestoration, 1000);
            });
          }, { once: true, signal: stabilizationController.signal });
        }
      }));
    }

    startScrollStabilization() {
      this.stopScrollStabilization();
      this.scrollStabilizationController = new AbortController();

      const cancelCorrection = () => {
        this.scrollCorrectionCancelled = true;
        this.stopScrollStabilization();
        this.releaseScrollRestoration();
      };

      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((eventName) => {
        window.addEventListener(eventName, cancelCorrection, {
          capture: true,
          passive: eventName !== 'keydown',
          signal: this.scrollStabilizationController.signal
        });
      });
    }

    stopScrollStabilization() {
      this.scrollStabilizationController?.abort();
      this.scrollStabilizationController = null;
      this.scrollResizeObserver?.disconnect();
      this.scrollResizeObserver = null;

      if (this.scrollAlignmentFrame != null && 'cancelAnimationFrame' in window) {
        window.cancelAnimationFrame(this.scrollAlignmentFrame);
      }

      this.scrollAlignmentFrame = null;

      if (this.scrollFinishTimer != null) {
        window.clearTimeout(this.scrollFinishTimer);
        this.scrollFinishTimer = null;
      }
    }

    releaseScrollRestoration(immediate = false) {
      if (!this.scrollRestorationOverridden) return;

      const releaseToken = (this.scrollRestorationReleaseToken || 0) + 1;
      this.scrollRestorationReleaseToken = releaseToken;

      if (this.scrollRestorationReleaseHandler) {
        window.removeEventListener('pageshow', this.scrollRestorationReleaseHandler);
        this.scrollRestorationReleaseHandler = null;
      }

      const release = () => {
        if (releaseToken !== this.scrollRestorationReleaseToken || !this.scrollRestorationOverridden) return;

        try {
          history.scrollRestoration = this.previousScrollRestoration || 'auto';
        } catch (error) {
          // No action is required when the property is unsupported.
        }

        this.scrollRestorationOverridden = false;
        this.previousScrollRestoration = null;
      };

      if (immediate) {
        release();
      } else if (document.readyState === 'complete') {
        requestAnimationFrame(() => window.setTimeout(release, 0));
      } else {
        this.scrollRestorationReleaseHandler = () => {
          this.scrollRestorationReleaseHandler = null;
          requestAnimationFrame(() => window.setTimeout(release, 0));
        };
        window.addEventListener('pageshow', this.scrollRestorationReleaseHandler, { once: true });
      }
    }

    getInitialSignature() {
      return Array.from(this.productGrid.children)
        .map((product) => product.getAttribute('handle') || '')
        .join('|');
    }

    getUrlKey() {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();
      return `${url.pathname}${url.search}`;
    }

    safeStorageGet(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    safeStorageSet(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    }

    safeStorageRemove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        // Storage can be unavailable in privacy modes; network replay remains available.
      }
    }
  }

  customElements.define(ELEMENT_NAME, CollectionInfiniteScroll);
})();
